import type { ResourcePermissionInput } from "src/components/permission/use-resource-permission";

/**
 * Native-delivery (CEP) RBAC grants, as `permission` descriptors ready to pass
 * to `Permissioned*` components and `useResourcePermission`. Defining them once
 * keeps gated controls consistent and lets a typo'd grant fail to compile.
 */
export const NATIVE_DELIVERY_PERMISSIONS = {
  view: { v2: { resource: "workspace", grant: "can_view_native_delivery" } },
  draft: { v2: { resource: "workspace", grant: "can_draft_native_delivery" } },
  manageMessages: {
    v2: { resource: "workspace", grant: "can_manage_native_delivery_messages" },
  },
  manageAssets: {
    v2: { resource: "workspace", grant: "can_manage_native_delivery_assets" },
  },
  manageChannels: {
    v2: { resource: "workspace", grant: "can_manage_native_delivery_channels" },
  },
  manageSettings: {
    v2: { resource: "workspace", grant: "can_manage_native_delivery_settings" },
  },
} satisfies Record<string, ResourcePermissionInput<"workspace">>;

/**
 * Grant required to publish staged message changes. On a campaign past Draft the
 * publish reaches recipients on the next dispatch wave, so it takes the manage
 * grant; the draft grant only covers staging changes for someone else to publish.
 */
export function getPublishPermission(
  isCampaignLive: boolean,
): ResourcePermissionInput<"workspace"> {
  return isCampaignLive
    ? NATIVE_DELIVERY_PERMISSIONS.manageMessages
    : NATIVE_DELIVERY_PERMISSIONS.draft;
}

/**
 * Tooltip copy shown when a control is disabled because the user lacks a
 * native-delivery grant. Centralized so the same wording is reused everywhere a
 * control is gated rather than duplicated per call site.
 */
export const NATIVE_DELIVERY_UNAUTHORIZED_TOOLTIPS = {
  draft:
    "You do not have permission to draft Native Delivery messages for this workspace",
  manageMessages:
    "You do not have permission to manage Native Delivery messages for this workspace",
  edit: "This journey contains a message node and you do not have permission to draft Native Delivery messages",
} as const;
